import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, ArrowRight, GraduationCap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useIsMobile } from '../../hooks/useIsMobile';

const G = '#0F5A3A';
const GOLD = '#F5B327';
const IVORY = '#F4F0E6';
const BTN_R = '8px';

const METRICS = [
  { label: 'College Matches', value: '12', color: G },
  { label: 'Scholarships', value: '8', color: '#b45309' },
  { label: 'Direct Admissions', value: '3', color: '#1d4ed8' },
  { label: 'New Items', value: '2', color: '#7c3aed' },
];

const OPPORTUNITIES = [
  { school: 'UC Berkeley', type: 'Direct Admission', match: '92%', due: 'May 1', dotColor: '#1d4ed8' },
  { school: 'Coca-Cola Scholars', type: 'Scholarship · $20,000', match: '88%', due: 'May 19', dotColor: GOLD },
  { school: 'Google STEP Internship', type: 'Internship · Summer', match: '85%', due: 'Jun 15', dotColor: '#4A5568' },
  { school: 'Howard University', type: 'Direct Admission', match: '94%', due: 'Jun 1', dotColor: '#003A63' },
];

const DEADLINES = [
  { name: 'Common App Submission', date: 'May 1', urgency: 'high' },
  { name: 'FAFSA Renewal', date: 'May 15', urgency: 'med' },
  { name: 'Scholarship Essay — Coca-Cola', date: 'May 19', urgency: 'med' },
];

const CHECKMARKS = [
  'Centralized hub for matches, applications, and deadlines',
  'Personalized match scoring across colleges and scholarships',
  'Deadline alerts and action reminders built in',
  'Secure, private, and always free for students',
];

export const PortalPreview = () => {
  const isMobile = useIsMobile();

  return (
    <section style={{ background: IVORY, padding: isMobile ? '48px 0' : '72px 0' }}>
      <div style={{ maxWidth: '1280px', margin: '0 auto', padding: isMobile ? '0 20px' : '0 40px' }}>
        <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.55 }} style={{ textAlign: 'center', marginBottom: isMobile ? '32px' : '48px' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.14em', color: G, marginBottom: '8px' }}>YOUR SIGNAL PROFILE</div>
          <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: isMobile ? '1.75rem' : 'clamp(1.9rem, 3vw, 2.7rem)', fontWeight: 900, color: G, lineHeight: 1.1, letterSpacing: '-0.02em', margin: 0 }}>
            Everything you need. In one smart dashboard.
          </h2>
        </motion.div>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.15fr 0.85fr', gap: isMobile ? '28px' : '48px', alignItems: 'center' }}>

          {/* Dashboard mockup */}
          <motion.div initial={{ opacity: 0, x: -16 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.65 }}
            style={{ background: '#fff', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 24px 72px rgba(15,90,58,0.13)', border: '1px solid rgba(15,90,58,0.07)', order: isMobile ? 2 : 0 }}
            data-testid="portal-dashboard-mockup">
            <div style={{ padding: '10px 16px', borderBottom: '1px solid #f0ede8', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fafaf8' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {['#ff5f57','#ffbd2e','#28ca41'].map((c, i) => <div key={i} style={{ width: '8px', height: '8px', borderRadius: '50%', background: c }} />)}
              </div>
              <div style={{ fontSize: '11px', fontWeight: 700, color: G }}>Student Signal — My Dashboard</div>
              <div style={{ width: '26px', height: '26px', borderRadius: '50%', background: `${G}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, color: G }}>M</div>
            </div>

            <div style={{ display: 'flex', height: isMobile ? '320px' : '440px', overflow: 'hidden' }}>
              {!isMobile && (
                <div style={{ width: '168px', background: `${G}07`, borderRight: '1px solid #f0ede8', padding: '16px 10px', flexShrink: 0 }}>
                  <div style={{ fontSize: '9px', fontWeight: 700, color: '#bbb', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '10px', paddingLeft: '8px' }}>Navigation</div>
                  {['Dashboard','Matches','Applications','Scholarships','Direct Admissions','First Briefcase','Alerts','Resources','Profile Settings'].map((item, i) => (
                    <div key={item} style={{ padding: '7px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: i === 0 ? 700 : 400, color: i === 0 ? '#fff' : '#4A5568', background: i === 0 ? G : 'transparent', marginBottom: '2px' }}>{item}</div>
                  ))}
                </div>
              )}
              <div style={{ flex: 1, padding: isMobile ? '14px' : '18px 16px', overflowY: 'auto', display: 'flex', gap: '14px' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: isMobile ? '13px' : '14px', fontWeight: 700, color: '#1a1a1a', marginBottom: '2px' }}>Welcome back, Maya</div>
                  <div style={{ fontSize: '11px', color: '#999', marginBottom: '10px' }}>Here&apos;s your latest signals and opportunities</div>
                  <div style={{ display: 'grid', gridTemplateColumns: `repeat(${isMobile ? 2 : 4},1fr)`, gap: '6px', marginBottom: '12px' }}>
                    {METRICS.map(m => (
                      <div key={m.label} style={{ padding: '7px', background: IVORY, borderRadius: '8px', textAlign: 'center', border: '1px solid #f0ede8' }}>
                        <div style={{ fontSize: isMobile ? '15px' : '17px', fontWeight: 900, color: m.color, lineHeight: 1 }}>{m.value}</div>
                        <div style={{ fontSize: '9px', color: '#888', marginTop: '2px', lineHeight: 1.3 }}>{m.label}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ fontSize: '10px', fontWeight: 700, color: '#bbb', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '6px' }}>Recent Opportunities</div>
                  {OPPORTUNITIES.slice(0, isMobile ? 3 : 4).map(opp => (
                    <div key={opp.school} style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '5px 7px', borderRadius: '7px', background: '#fafaf9', marginBottom: '4px', border: '1px solid #f0ede8' }}>
                      <div style={{ width: '24px', height: '24px', borderRadius: '6px', background: `${opp.dotColor}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <GraduationCap size={11} color={opp.dotColor} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '11px', fontWeight: 700, color: '#1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{opp.school}</div>
                        <div style={{ fontSize: '9px', color: '#999' }}>{opp.type}</div>
                      </div>
                      {!isMobile && <span style={{ fontSize: '10px', fontWeight: 700, color: G, background: `${G}10`, padding: '2px 6px', borderRadius: '4px', flexShrink: 0 }}>Match {opp.match}</span>}
                    </div>
                  ))}
                </div>
                {!isMobile && (
                  <div style={{ width: '140px', flexShrink: 0 }}>
                    <div style={{ fontSize: '10px', fontWeight: 700, color: '#bbb', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '8px' }}>Upcoming Deadlines</div>
                    {DEADLINES.map(d => (
                      <div key={d.name} style={{ padding: '8px', borderRadius: '7px', background: IVORY, border: '1px solid #f0ede8', marginBottom: '6px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
                          <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: d.urgency === 'high' ? '#ef4444' : GOLD, flexShrink: 0 }} />
                          <span style={{ fontSize: '9px', fontWeight: 700, color: d.urgency === 'high' ? '#ef4444' : '#b45309' }}>{d.urgency === 'high' ? 'Urgent' : 'Soon'}</span>
                        </div>
                        <div style={{ fontSize: '11px', fontWeight: 600, color: '#1a1a1a', lineHeight: 1.3, marginBottom: '2px' }}>{d.name}</div>
                        <div style={{ fontSize: '10px', color: '#999' }}>{d.date}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </motion.div>

          {/* Copy */}
          <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.65, delay: 0.12 }} style={{ order: isMobile ? 1 : 0 }}>
            <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.14em', color: GOLD, marginBottom: '10px' }}>YOUR JOURNEY, ORGANIZED</div>
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: isMobile ? '1.6rem' : 'clamp(1.7rem, 2.8vw, 2.4rem)', fontWeight: 900, color: G, lineHeight: 1.1, letterSpacing: '-0.02em', marginBottom: '14px' }}>
              Everything you need in one smart dashboard.
            </h2>
            <p style={{ fontSize: '15px', color: '#6E6A61', lineHeight: 1.75, marginBottom: '24px' }}>
              Your Signal Profile is more than a form — it&apos;s your living intelligence layer. The more complete it is, the stronger your matches and the better your outcomes.
            </p>
            <div style={{ marginBottom: '28px' }}>
              {CHECKMARKS.map((item) => (
                <div key={item} style={{ display: 'flex', alignItems: 'flex-start', gap: '9px', marginBottom: '10px' }}>
                  <CheckCircle2 size={16} color={G} style={{ flexShrink: 0, marginTop: '3px' }} />
                  <span style={{ fontSize: '14px', color: '#4A5568', lineHeight: 1.6 }}>{item}</span>
                </div>
              ))}
            </div>
            <Link to="/signup" data-testid="portal-preview-cta" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '13px 24px', background: G, color: '#fff', fontWeight: 700, fontSize: '14px', borderRadius: BTN_R, textDecoration: 'none' }}>
              Explore the Dashboard <ArrowRight size={15} />
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default PortalPreview;
